import { ref, watch, onUnmounted } from 'vue';

/**
 * useSearchDebounce — centralized search debounce.
 *
 * Returns two refs:
 *  - search:            immediate (bound to v-model)
 *  - debouncedSearch:   delayed (watch this to trigger fetch)
 *
 * Empty string bypasses delay — resets immediately.
 * Any non-empty string triggers search after the debounce delay.
 *
 * @param {object}   [options]
 * @param {number}   [options.delay=600]     — idle wait in ms
 * @returns {{ search: import('vue').Ref<string>, debouncedSearch: import('vue').Ref<string> }}
 */
export function useSearchDebounce({ delay = 600 } = {}) {
    const search = ref('');
    const debouncedSearch = ref('');

    let timer = null;

    watch(search, val => {
        clearTimeout(timer);

        if (val === '') {
            debouncedSearch.value = '';
            return;
        }

        timer = setTimeout(() => {
            debouncedSearch.value = val;
        }, delay);
    });

    onUnmounted(() => {
        clearTimeout(timer);
    });

    return { search, debouncedSearch };
}
